import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchApplicationDetail, type ApplicationDetail as AppDetail } from '../lib/applicationService';
import {
  fetchCandidateProfile,
  fetchOfferDetail,
  type CandidateProfileDetail,
  type OfferDetailData,
} from '../lib/engagementService';
import { logger } from '../lib/logger';

const statusStyle: Record<string, { label: string; cls: string }> = {
  pending: { label: 'En attente', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  viewed: { label: 'Vue', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  selected: { label: 'Sélectionné', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Refusée', cls: 'bg-red-50 text-red-600 border-red-200' },
};

function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400';
  if (score >= 70) return 'text-emerald-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-500';
}

function Card({ title, accent, children, action }: {
  title: string;
  accent: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 flex flex-col">
      <div className={`px-5 py-3 border-b border-gray-100 flex items-center justify-between rounded-t-xl ${accent}`}>
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        {action}
      </div>
      <div className="p-5 flex-1">{children}</div>
    </div>
  );
}

function Pills({ items, tone }: { items: string[]; tone: 'green' | 'red' }) {
  if (items.length === 0) return <span className="text-xs text-gray-400">—</span>;
  const cls = tone === 'green'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-red-50 text-red-600 border-red-200';
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((s) => (
        <span key={s} className={`px-2 py-0.5 text-[11px] font-medium rounded-full border ${cls}`}>{s}</span>
      ))}
    </div>
  );
}

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AppDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down (aller plus loin)
  const [candExpanded, setCandExpanded] = useState(false);
  const [offerExpanded, setOfferExpanded] = useState(false);
  const [candFull, setCandFull] = useState<CandidateProfileDetail | null>(null);
  const [offerFull, setOfferFull] = useState<OfferDetailData | null>(null);
  const [candLoading, setCandLoading] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        setDetail(await fetchApplicationDetail(id));
      } catch (err) {
        logger.error('load_application_detail', 'ApplicationDetail', { error: String(err) });
        setError('Candidature introuvable');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const toggleCandidate = async () => {
    const next = !candExpanded;
    setCandExpanded(next);
    if (next && !candFull && detail?.candidate.userId) {
      setCandLoading(true);
      try {
        setCandFull(await fetchCandidateProfile(detail.candidate.userId));
      } catch (err) {
        logger.error('load_candidate_full', 'ApplicationDetail', { error: String(err) });
      } finally {
        setCandLoading(false);
      }
    }
  };

  const toggleOffer = async () => {
    const next = !offerExpanded;
    setOfferExpanded(next);
    if (next && !offerFull && detail?.offer.id) {
      setOfferLoading(true);
      try {
        setOfferFull(await fetchOfferDetail(detail.offer.id));
      } catch (err) {
        logger.error('load_offer_full', 'ApplicationDetail', { error: String(err) });
      } finally {
        setOfferLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">{error || 'Introuvable'}</div>
      </div>
    );
  }

  const st = statusStyle[detail.status] || { label: detail.status, cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  const c = detail.candidate;
  const o = detail.offer;
  const comp = detail.compatibility;
  const candName = `${c.firstName} ${c.lastName}`.trim() || 'Candidat';

  return (
    <div className="space-y-5">
      {/* Bandeau */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${st.cls}`}>{st.label}</span>
          <span>Postulé le {detail.appliedAt}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
        <span>{candName}</span>
        <span className="text-gray-300">→</span>
        <span>{o.positionName || 'Offre'}</span>
        {o.employerName && <span className="text-sm font-normal text-gray-400">· {o.employerName}</span>}
      </div>

      {/* 3 aspects */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* CANDIDAT */}
        <Card
          title="👤 Profil candidat"
          accent="bg-blue-50/60"
          action={<button onClick={toggleCandidate} className="text-xs font-medium text-blue-600 hover:text-blue-800">{candExpanded ? 'Réduire' : 'Aller plus loin →'}</button>}
        >
          <div className="flex items-center gap-3 mb-3">
            {c.photoUrl ? (
              <img src={c.photoUrl} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-xl text-gray-400">👤</div>
            )}
            <div className="min-w-0">
              <p className="font-semibold text-gray-900 truncate">{candName}</p>
              <p className="text-xs text-gray-500">{c.city || '—'}</p>
            </div>
          </div>

          {candExpanded && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              {candLoading ? (
                <p className="text-xs text-gray-400">Chargement…</p>
              ) : candFull ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{candFull.stats.applicationsCount}</p><p className="text-[10px] text-gray-400">Candidatures</p></div>
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{candFull.stats.profileViewsReceived}</p><p className="text-[10px] text-gray-400">Vues profil</p></div>
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{candFull.videos.length}</p><p className="text-[10px] text-gray-400">Vidéos</p></div>
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{candFull.stats.totalLikes}</p><p className="text-[10px] text-gray-400">Likes</p></div>
                  </div>
                  {candFull.email && <p className="text-xs text-gray-500">✉️ {candFull.email}</p>}
                  {candFull.isSeed && <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">Seed · {candFull.seedTag}</span>}
                </div>
              ) : (
                <p className="text-xs text-gray-400">Détail indisponible</p>
              )}
            </div>
          )}
        </Card>

        {/* ANNONCE */}
        <Card
          title="📋 Annonce"
          accent="bg-amber-50/60"
          action={<button onClick={toggleOffer} className="text-xs font-medium text-amber-700 hover:text-amber-900">{offerExpanded ? 'Réduire' : 'Aller plus loin →'}</button>}
        >
          <p className="font-semibold text-gray-900">{o.positionName || '—'}</p>
          <p className="text-xs text-gray-500 mt-0.5">{o.employerName || '—'}{o.city ? ` · ${o.city}` : ''}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {o.contractType && <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-600">{o.contractType.toUpperCase()}</span>}
            {(o.salaryMin || o.salaryMax) && (
              <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-600">
                {o.salaryMin || '?'}{o.salaryMax ? `–${o.salaryMax}` : ''} €
              </span>
            )}
          </div>

          {offerExpanded && (
            <div className="mt-3 border-t border-gray-100 pt-3">
              {offerLoading ? (
                <p className="text-xs text-gray-400">Chargement…</p>
              ) : offerFull ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{offerFull.stats.applications}</p><p className="text-[10px] text-gray-400">Candidatures</p></div>
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{offerFull.stats.favorites}</p><p className="text-[10px] text-gray-400">Favoris</p></div>
                    <div className="bg-gray-50 rounded-lg py-2"><p className="text-base font-bold text-gray-800">{offerFull.stats.swipes}</p><p className="text-[10px] text-gray-400">Swipes</p></div>
                  </div>
                  {offerFull.description && <p className="text-xs text-gray-600 whitespace-pre-line line-clamp-6">{offerFull.description}</p>}
                  {offerFull.isSeed && <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">Seed · {offerFull.seedTag}</span>}
                </div>
              ) : (
                <p className="text-xs text-gray-400">Détail indisponible</p>
              )}
            </div>
          )}
        </Card>

        {/* COMPATIBILITÉ */}
        <Card title="🎯 Compatibilité" accent="bg-emerald-50/60">
          <div className="flex items-center gap-4 mb-4">
            <div className={`text-4xl font-bold ${scoreColor(comp.score)}`}>
              {comp.score === null ? '—' : `${comp.score}%`}
            </div>
            <p className="text-xs text-gray-500">
              {comp.score === null
                ? 'Aucun critère défini sur l\'offre'
                : 'Estimation : compétences (60%) · secteur (25%) · langues (15%)'}
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-[11px] font-semibold text-gray-500 mb-1">
                Compétences ({comp.skills.matched.length}/{comp.skills.required})
              </p>
              <Pills items={comp.skills.matched} tone="green" />
              {comp.skills.missing.length > 0 && (
                <div className="mt-1"><p className="text-[10px] text-gray-400 mb-1">Manquantes</p><Pills items={comp.skills.missing} tone="red" /></div>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm">
              <span className="text-[11px] font-semibold text-gray-500">Secteur</span>
              {comp.sectorMatch === null ? (
                <span className="text-xs text-gray-400">non spécifié</span>
              ) : comp.sectorMatch ? (
                <span className="text-xs text-emerald-600 font-medium">✓ correspond</span>
              ) : (
                <span className="text-xs text-red-500 font-medium">✗ différent</span>
              )}
            </div>

            {comp.languages.required > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-gray-500 mb-1">
                  Langues ({comp.languages.matched.length}/{comp.languages.required})
                </p>
                <Pills items={comp.languages.matched} tone="green" />
                {comp.languages.missing.length > 0 && (
                  <div className="mt-1"><Pills items={comp.languages.missing} tone="red" /></div>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ApplicationDetail;
